// Tutor Profile Page
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/TutorProfile.css";
import { Helmet } from "react-helmet-async";

import Footer from "../Footer";
import { UserContext } from "../context/UserContext";

import {
  BadgeCheck,
  MapPinCheck,
  Clock9,
  Phone,
  GraduationCap,
  CalendarCheck,
  UserRound,
} from "lucide-react";

// --- Loading Skeleton (Profile) ---
const TutorProfileSkeleton = React.memo(function TutorProfileSkeleton() {
  return (
    <div className="tutor-profile-page">
      <div className="tutor-card tn-skeleton-card">
        <div className="tutor-image">
          <div className="tn-skeleton tn-skeleton-avatar" />
        </div>

        <div className="tutor-info">
          <div className="tutor-header">
            <div
              className="tnv-inline"
              style={{ gap: 10, alignItems: "center" }}
            >
              <div
                className="tn-skeleton tn-skeleton-line"
                style={{ width: "42%", height: 28 }}
              />
              <span className="tn-skeleton tn-skeleton-badge" />
            </div>
          </div>

          <div className="qualification" style={{ marginTop: 10 }}>
            <div
              className="tn-skeleton tn-skeleton-line"
              style={{ width: "65%", height: 16 }}
            />
          </div>

          <div className="tutor-meta" style={{ marginTop: 12 }}>
            <div
              className="tn-skeleton tn-skeleton-line"
              style={{ width: "60%", height: 14, marginBottom: 8 }}
            />
            <div
              className="tn-skeleton tn-skeleton-line"
              style={{ width: "40%", height: 14, marginBottom: 8 }}
            />
            <div
              className="tn-skeleton tn-skeleton-line"
              style={{ width: "50%", height: 14 }}
            />
          </div>

          <div
            className="tn-skeleton tn-skeleton-paragraph"
            style={{ marginTop: 14 }}
          />

          <div className="sml" style={{ marginTop: 10 }}>
            <div className="tn-skeleton-chip-row">
              <span className="tn-skeleton tn-skeleton-chip" />
              <span className="tn-skeleton tn-skeleton-chip" />
              <span className="tn-skeleton tn-skeleton-chip" />
            </div>
            <div className="tn-skeleton-chip-row">
              <span className="tn-skeleton tn-skeleton-chip" />
              <span className="tn-skeleton tn-skeleton-chip" />
            </div>
          </div>

          <div className="tutor-actions" style={{ marginTop: 16 }}>
            <div className="tn-skeleton tn-skeleton-button" />
          </div>
        </div>
      </div>
    </div>
  );
});

// SEO
function buildTutorJsonLd(t) {
  const subjects = Array.isArray(t.subjects) ? t.subjects : [];
  const primaryArea =
    Array.isArray(t.areas) && t.areas.length ? t.areas[0] : null;
  const address = primaryArea
    ? {
        "@type": "PostalAddress",
        addressLocality: primaryArea.district || "Bangladesh",
        addressRegion: primaryArea.division || undefined,
        addressCountry: "BD",
      }
    : t.location
      ? {
          "@type": "PostalAddress",
          addressLocality: t.location,
          addressCountry: "BD",
        }
      : undefined;

  const json = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: t.name,
    image: t.profile_picture || undefined,
    jobTitle: "Private Tutor",
    knowsAbout: subjects,
    teaches: subjects,
    areaServed: [
      ...(Array.isArray(t.areas) ? t.areas.map((a) => a.name).slice(0, 6) : []),
      "Bangladesh",
    ].filter(Boolean),
    address,
    url: `https://tn.goatteachers.com/tutor/${t.id}`,
  };

  if (
    typeof t.rating === "number" &&
    t.rating > 0 &&
    Number.isFinite(t.rating)
  ) {
    json.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: t.rating.toFixed(1),
      reviewCount: String(t.reviewCount || 1),
    };
  }
  return json;
}

const TutorProfile = () => {
  const { id } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [showPhone, setShowPhone] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);

  const formatTutorData = (tutor) => {
    return {
      id: tutor.id,
      name: tutor.name,
      is_verified: !!tutor.is_verified,
      profile_picture: tutor.profile_picture,
      gender: tutor.gender,
      bio: tutor.bio,
      teaching_mode: tutor.teaching_mode,
      areas:
        tutor.areas?.map(
          (a) =>
            `${a.name}${a.district ? ", " + a.district : ""}${
              a.division ? " (" + a.division + ")" : ""
            }`,
        ) || [],
      location: [
        tutor.location?.area,
        tutor.location?.district,
        tutor.location?.division,
      ]
        .filter(Boolean)
        .join(", "),
      availability: tutor.availability || [],
      phone_number: tutor.connect?.phone || "",
      whatsapp_number: tutor.connect?.whatsapp || "",
      subjects: tutor.subjects || [],
      mediums: tutor.mediums || [],
      levels: tutor.levels || [],
      degree: tutor.degree || "—",
      institute: tutor.institute || "—",
      field_of_study: tutor.field_of_study || "—",
      start_year: tutor.study_period?.split("–")[0] || "",
      end_year: tutor.study_period?.split("–")[1] || "",
      slug: `tutor-${tutor.id}`,
      rating: tutor.rating || "4.8",
    };
  };

  // BIO Sanitization
  const sanitizeBio = (bio = "") => {
    return bio.replace(
      /(\+?88)?\s?0?(1[1-9]\d{8,9})|\b\d{11,14}\b/g,
      "[ Contact Hidden ]",
    );
  };

  useEffect(() => {
    const fetchTutor = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://tn.server.goatteachers.com/api/tutors/${id}`,
          {
            headers:
              user && user.token
                ? { Authorization: `Bearer ${user.token}` }
                : {},
          },
        );

        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error("Failed to parse server response");
        }

        if (!response.ok || data.success === false) {
          throw new Error(data.message || "Failed to load tutor");
        }

        setTutor(data.tutor);
      } catch (error) {
        console.error("Error fetching tutor:", error.message);
        setTutor(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTutor();
  }, [id, user]);

  if (loading) return <TutorProfileSkeleton />;
  if (!tutor) return <p>Tutor not found.</p>;

  // SEO 02
  const primaryArea =
    Array.isArray(tutor.areas) && tutor.areas.length ? tutor.areas[0] : null;
  const city = primaryArea?.district || tutor.location || "Bangladesh";
  const subjects = Array.isArray(tutor.subjects) ? tutor.subjects : [];
  const subjectLine = subjects.length ? subjects.join(", ") : "Private Tutor";

  const pageTitle = `${tutor.name} — ${subjectLine} in ${city} | Goatt`;
  const pageDesc =
    tutor.bio && tutor.bio.trim().length > 0
      ? tutor.bio.replace(/\s+/g, " ").slice(0, 150)
      : `${tutor.name} • ${subjectLine} • ${city}. Verified profile, education, areas served, and availability.`;

  const canonicalUrl = `https://tn.goatteachers.com/tutor/${id}`;
  const ogImage =
    tutor.profile_picture ||
    "https://tn.goatteachers.com/assets/tutor-default.png";

  const isThin =
    !tutor.name ||
    !subjects.length ||
    (!tutor.bio && (!tutor.mediums || tutor.mediums.length === 0));

  const jsonLd = buildTutorJsonLd({ ...tutor, id });

  return (
    <>
      {/* SEO  */}

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />

        {isThin && <meta name="robots" content="noindex,follow" />}

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="tutor-profile-page">
        <div className="tutor-card">
          <div className="tutor-image">
            <div className="avatar">
              {tutor.profile_picture ? (
                <img src={tutor.profile_picture} alt={tutor.name} />
              ) : (
                <div className="avatar-placeholder">
                  {tutor.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className="tutor-info">
            <div className="tutor-header">
              <div className="tnv-inline">
                <h2 className="tutor-name">{tutor.name}</h2>
                {tutor.is_verified && (
                  <span className="tnv-badge-chip" title="University Verified">
                    <BadgeCheck size={14} aria-hidden="true" />
                    <span>University Verified</span>
                  </span>
                )}
              </div>
            </div>

            <div className="qualification">
              <span className="iconBox">
                <GraduationCap size={30} className="iconWhite" />
              </span>
              <span>
                {tutor.degree} in {tutor.field_of_study} — {tutor.institute}
              </span>
            </div>

            <div className="tutor-meta">
              {tutor.areas.length > 0 ? (
                <div className="meta-item location">
                  <span className="iconBox">
                    <MapPinCheck size={30} className="iconWhite" />
                  </span>
                  <span>
                    {tutor.areas.map((area) => area.name).join(" | ")} -{" "}
                    {tutor.areas[0].district} ({tutor.areas[0].division})
                  </span>
                </div>
              ) : tutor.location ? (
                <div className="meta-item location">
                  <span>{tutor.location}</span>
                </div>
              ) : null}

              {tutor.gender && (
                <div className="meta-item gender">
                  <UserRound size={30} className="iconWhite" />
                  <span>{tutor.gender}</span>
                </div>
              )}

              {tutor.availability.length > 0 && (
                <div className="meta-item availability">
                  <span>
                    {Array.isArray(tutor.availability) &&
                      tutor.availability.length > 0 && (
                        <div className="meta-item availability">
                          <span className="iconBox">
                            <CalendarCheck size={30} className="iconWhite" />
                          </span>
                          <div className="availability-list">
                            {tutor.availability.map((day, i) => (
                              <span key={`${day}-${i}`} className="time-slot">
                                {day}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                  </span>
                </div>
              )}
            </div>

            {/* Bio */}
            {tutor.bio && <p className="bio">{sanitizeBio(tutor.bio)}</p>}

            <div className="sml">
              {tutor.mediums.length > 0 && (
                <div className="subjects">
                  <div className="subject-label">Medium:</div>
                  <div className="indiana-scroll-container--hide-scrollbars">
                    {tutor.mediums.map((medium, i) => (
                      <span key={i} className="subject-tag mode-tag">
                        {medium}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tutor.subjects.length > 0 && (
                <div className="subjects">
                  <div className="subject-label">Subjects:</div>
                  <div className="indiana-scroll-container--hide-scrollbars">
                    {tutor.subjects.map((subject, i) => (
                      <span key={i} className="subject-tag">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tutor.levels.length > 0 && (
                <div className="subjects">
                  <div className="subject-label">Levels:</div>
                  <div className="indiana-scroll-container--hide-scrollbars">
                    {tutor.levels.map((level, i) => (
                      <span key={i} className="subject-tag level-tag">
                        {level}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="tutor-actions">
              {user ? (
                <>
                  {tutor.connect?.phone && (
                    <div
                      className="btn-call-tutor"
                      onClick={async () => {
                        try {
                          if (!user) return navigate("/login");
                          if (!showPhone) {
                            await fetch(
                              `https://tn.server.goatteachers.com/api/tutors/${id}/reveal`,
                              {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${user.token}`,
                                },
                                body: JSON.stringify({ method: "phone" }),
                              },
                            );
                          }
                          setShowPhone((prev) => !prev);
                        } catch (err) {
                          console.error("Reveal phone failed:", err);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <Phone size={16} />
                      <span>
                        {showPhone ? tutor.connect.phone : "ফোন নম্বর দেখো"}
                      </span>
                    </div>
                  )}

                  {tutor.connect?.whatsapp && (
                    <div
                      className="btn-call-tutor whatsapp"
                      onClick={async () => {
                        try {
                          if (!user) return navigate("/login");
                          if (!showWhatsapp) {
                            await fetch(
                              `https://tn.server.goatteachers.com/api/tutors/${id}/reveal`,
                              {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${user.token}`,
                                },
                                body: JSON.stringify({ method: "whatsapp" }),
                              },
                            );
                          }
                          setShowWhatsapp((prev) => !prev);
                        } catch (err) {
                          console.error("Reveal whatsapp failed:", err);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <Phone size={16} />
                      <span>
                        {showWhatsapp
                          ? tutor.connect.whatsapp
                          : "হোয়াটস্যাপ নম্বর দেখো"}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <button
                  className="btn-call-tutor"
                  onClick={() => navigate("/login")}
                >
                  <span>📞 Join as student</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TutorProfile;
